import { fromMinor, RULE_TEMPLATES, FEE_TEMPLATES, toMinor } from "./api";

export const EXISTING_COHORT = "Existing";

export interface CohortForm {
  uid: string;
  cohortName: string;
  ruleType: string;
  ruleParamsText: string;
  feeType: string;
  feeParamsText: string;
  oneTime: boolean;
  minSpend: string;
  holdoutShareBp: number;
  transactionCap: string;
  cohortCap: string;
}

export interface VersionForm {
  startDate: string;
  endDate: string;
  cohortDefinitionVersion: string;
  networks: string[];
  campaignCap: string;
  capSplit: string;
  vatPercent: string;
  activationRequired: boolean;
  forecastRewardsPerDay: string;
  afterClearRefund: string;
  cohorts: CohortForm[];
}

export const AFTER_CLEAR_REFUNDS: Record<string, string> = {
  customer: "Take the reward back from the customer",
};

const json = (value: object) => JSON.stringify(value, null, 2);

let uidCounter = 0;
const nextUid = (): string => `cohort-${Date.now()}-${uidCounter++}`;

export const newCohort = (name: string, ruleType: string, rule: object, feeType: string, fee: object, oneTime: boolean): CohortForm => ({
  uid: nextUid(),
  cohortName: name,
  ruleType,
  ruleParamsText: json(rule),
  feeType,
  feeParamsText: json(fee),
  oneTime,
  minSpend: "",
  holdoutShareBp: 500,
  transactionCap: "",
  cohortCap: "",
});

export const blankCohort = (): CohortForm =>
  newCohort("", "A", RULE_TEMPLATES.A, "M1", FEE_TEMPLATES.M1, true);

export const DEFAULT_VERSION_FORM: VersionForm = {
  startDate: "",
  endDate: "",
  cohortDefinitionVersion: "",
  networks: ["visa"],
  campaignCap: "",
  capSplit: "pro_rata",
  vatPercent: "0",
  activationRequired: true,
  forecastRewardsPerDay: "",
  afterClearRefund: "customer",
  cohorts: [
    newCohort("Existing", "D", { rateBp: 200 }, "M2", { rateBp: 100 }, false),
    newCohort("Lapsed", "A", { rateBp: 300 }, "M1", { rateBp: 250 }, true),
    newCohort("Competitor", "A", { rateBp: 1000 }, "M1", { rateBp: 400 }, true),
    newCohort("New", "A", { rateBp: 400 }, "M1", { rateBp: 250 }, true),
  ],
};

const withExisting = (cohorts: CohortForm[]): CohortForm[] =>
  cohorts.some((c) => c.cohortName === EXISTING_COHORT)
    ? cohorts
    : [newCohort(EXISTING_COHORT, "D", { rateBp: 200 }, "M2", { rateBp: 100 }, false), ...cohorts];

export const fromVersion = (v: any): VersionForm => ({
  startDate: v.start_date || "",
  endDate: v.end_date || "",
  cohortDefinitionVersion: v.cohort_definition_version ? String(v.cohort_definition_version) : "",
  networks: v.networks || [],
  campaignCap: fromMinor(v.campaign_cap_minor),
  capSplit: v.cap_split,
  vatPercent: String(Number(v.vat_rate_bp) / 100),
  activationRequired: Boolean(Number(v.activation_required)),
  forecastRewardsPerDay: v.forecast_rewards_per_day === null ? "" : String(v.forecast_rewards_per_day),
  afterClearRefund: v.after_clear_refund in AFTER_CLEAR_REFUNDS ? v.after_clear_refund : "customer",
  cohorts: withExisting((v.cohorts || []).map((c: any) => ({
    uid: nextUid(),
    cohortName: c.cohort_name,
    ruleType: c.ruleType,
    ruleParamsText: json(c.ruleParams),
    feeType: c.feeType,
    feeParamsText: json(c.feeParams),
    oneTime: c.oneTime,
    minSpend: fromMinor(c.minSpendMinor || null),
    holdoutShareBp: c.holdoutShareBp,
    transactionCap: fromMinor(c.transactionCapMinor),
    cohortCap: fromMinor(c.cohortCapMinor),
  }))),
});

const parseParams = (text: string, label: string): object => {
  try {
    const value = JSON.parse(text);
    if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error();
    return value;
  } catch {
    throw new Error(`${label} must be a JSON object`);
  }
};

export const toVersionBody = (form: VersionForm): any => ({
  startDate: form.startDate,
  endDate: form.endDate,
  cohortDefinitionVersion: form.cohortDefinitionVersion.trim() ? Number(form.cohortDefinitionVersion) : null,
  networks: form.networks,
  campaignCapMinor: toMinor(form.campaignCap),
  capSplit: form.capSplit,
  vatRateBp: Math.round(Number(form.vatPercent || 0) * 100),
  activationRequired: form.activationRequired,
  forecastRewardsPerDay: form.forecastRewardsPerDay.trim() === "" ? null : Number(form.forecastRewardsPerDay),
  afterClearRefund: form.afterClearRefund,
  cohorts: form.cohorts.map((c) => ({
    cohortName: c.cohortName.trim(),
    ruleType: c.ruleType,
    ruleParams: parseParams(c.ruleParamsText, `${c.cohortName || "Cohort"} rule settings`),
    feeType: c.feeType,
    feeParams: parseParams(c.feeParamsText, `${c.cohortName || "Cohort"} fee settings`),
    oneTime: c.cohortName.trim() === EXISTING_COHORT ? false : c.oneTime,
    minSpendMinor: toMinor(c.minSpend) ?? 0,
    holdoutShareBp: c.holdoutShareBp,
    transactionCapMinor: toMinor(c.transactionCap),
    cohortCapMinor: toMinor(c.cohortCap),
  })),
});
